import React, { useState, useEffect } from 'react';
import { ProductInfo } from 'src/components/ProductInfo';
import { ProductSlider } from 'src/components/ProductSlider';
import { AuthWrapper } from 'src/features/core/auth/screen/AuthWrapper';
import { Product } from 'src/model/Product';
import Client from 'src/model/User';
import { SelectGrade, SelectSize } from '..';
import { ProductReviews } from 'src/components/ProductReview';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { api } from 'src/services/api';
import { PaymentMethodsModal } from 'src/components/PaymentMethodsModal';
import {
  amex,
  boleto,
  elo,
  master,
  pix,
  visa,
} from 'src/assets/images/payments-img';

interface ProductUIProps {
  products: Product;
  quantity: number;
  selectedImage: string;
  selectedColor: SelectGrade;
  selectedSize: SelectSize;
  user: Client;
  shippingCost: string | null;
  zoomStyle: React.CSSProperties;
  onAddCart: () => void;
  quantityChange: (increment: boolean) => void;
  handleColorSelect: (color?: string, image?: string, id?: string) => void;
  handleSizeSelect: (size: string, id: string) => void;
  handleMouseMove: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  handleMouseLeave: () => void;
  onChat: (sellerId: string) => void;
}

interface ProductReview {
  stars: number;
}

export const ProductUI: React.FC<ProductUIProps> = ({
  products,
  quantity,
  user,
  shippingCost,
  selectedImage,
  selectedColor,
  selectedSize,
  zoomStyle,
  onAddCart,
  quantityChange,
  handleColorSelect,
  handleSizeSelect,
  handleMouseMove,
  handleMouseLeave,
  onChat,
}): JSX.Element => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [reviews, setReviews] = useState<ProductReview[]>([]);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!products.id) return;
      try {
        const response = await api.get(`/review/find-all/${products.id}`);
        setReviews(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar reviews:', error);
      }
    };
    fetchReviews();
  }, [products.id]);

  const calculateAverageStars = () => {
    if (reviews.length === 0) return 0;
    const totalStars = reviews.reduce((sum, review) => sum + review.stars, 0);
    return totalStars / reviews.length;
  };

  const averageStars = calculateAverageStars();
  const totalReviews = reviews.length;

  const shouldTruncate =
    products.description && products.description.length > 200;
  const displayDescription =
    isDescriptionExpanded || !shouldTruncate
      ? products.description
      : products.description?.substring(0, 200) + '...';

  return (
    <AuthWrapper>
      {products && (
        <div className="flex flex-wrap w-full container md:gap-4 bg-white p-0 md:p-4 rounded-lg shadow-sm">
          <div className="w-full md:h-[500px] lg:hidden lg:flex-none">
            <ProductSlider
              imagens={products.imagens}
              highlightedImage={selectedImage}
              zoomStyle={zoomStyle}
              handleColorSelect={handleColorSelect}
              handleMouseMove={handleMouseMove}
              handleMouseLeave={handleMouseLeave}
            />
          </div>
          <div className="w-full lg:hidden lg:flex-none">
            <ProductInfo
              products={products}
              addCart={onAddCart}
              quantity={quantity}
              quantityChange={quantityChange}
              user={user}
              shippingCost={shippingCost}
              selectedColor={selectedColor}
              selectedSize={selectedSize}
              handleColorSelect={(color, image, id) =>
                handleColorSelect(color, image, id)
              }
              handleSizeSelect={handleSizeSelect}
              onChat={onChat}
              averageStars={averageStars}
              totalReviews={totalReviews}
            />
          </div>

          <div className="hidden flex-none lg:grid w-full lg:grid-cols-[55%_45%] gap-2">
            <div className="w-full">
              <ProductSlider
                imagens={products.imagens}
                zoomStyle={zoomStyle}
                highlightedImage={selectedImage}
                handleColorSelect={handleColorSelect}
                handleMouseMove={handleMouseMove}
                handleMouseLeave={handleMouseLeave}
              />
            </div>
            <div className="w-full">
              <ProductInfo
                products={products}
                addCart={onAddCart}
                quantity={quantity}
                quantityChange={quantityChange}
                user={user}
                shippingCost={shippingCost}
                selectedColor={selectedColor}
                selectedSize={selectedSize}
                handleColorSelect={(color, image, id) =>
                  handleColorSelect(color, image, id)
                }
                handleSizeSelect={handleSizeSelect}
                onChat={onChat}
                averageStars={averageStars}
                totalReviews={totalReviews}
              />
            </div>
          </div>

          <div className="w-full gap-4 px-0">
            <div>
              <hr className="border-t-2 border-gray-300 my-4" />
            </div>

            {products.description && (
              <div className="bg-white p-4 mb-4">
                <h3 className="text-lg font-semibold mb-3">
                  Descrição do Produto
                </h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {displayDescription}
                </p>
                {shouldTruncate && (
                  <button
                    onClick={() =>
                      setIsDescriptionExpanded(!isDescriptionExpanded)
                    }
                    className="flex items-center gap-1 text-sm text-blue-600 mt-3 hover:underline"
                  >
                    {isDescriptionExpanded ? (
                      <>
                        <span>Ver menos</span> <ChevronUp size={14} />
                      </>
                    ) : (
                      <>
                        <span>Ver mais</span> <ChevronDown size={14} />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            <div>
              <hr className="border-t-2 border-gray-300 my-4" />
            </div>
            <div className="bg-white p-4 mb-4">
              <h3 className="text-lg font-semibold mb-3">Meios de pagamento</h3>
              <div className="mb-3">
                <p className="text-sm text-gray-700">
                  Divida em até 12x no cartão |{' '}
                  <span className="text-teal-600 font-medium">
                    2x sem juros
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <img
                  src={master}
                  alt="Mastercard"
                  className="h-8 object-contain"
                />
                <img src={boleto} alt="Boleto" className="h-8 object-contain" />
                <img
                  src={amex}
                  alt="American Express"
                  className="h-8 object-contain"
                />
                <img src={elo} alt="Elo" className="h-8 object-contain" />
                <img src={visa} alt="Visa" className="h-8 object-contain" />
                <img src={pix} alt="Pix" className="h-8 object-contain" />
              </div>
              <button
                onClick={() => setIsPaymentModalOpen(true)}
                className="flex items-center gap-1 text-blue-600 text-sm hover:underline"
              >
                Ver mais meios de pagamento
                <ChevronRight size={16} />
              </button>
            </div>

            <div>
              <hr className="border-t-2 border-gray-300 my-4" />
            </div>

            <ProductReviews productId={products.id} />
          </div>
        </div>
      )}

      <PaymentMethodsModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />
    </AuthWrapper>
  );
};
